// 世界史スポットの解説モーダル。index.html の #site-modal を操作するだけで、
// ゲーム側の状態（進捗の永続化・ポーズ）には触れない。数値は open() で受け取る。
//
// createHud / createLocationPicker / createMinimap と同じく、
// 「引数はオブジェクト1個・戻り値はメソッド袋・dispose() を持つ」ファクトリ形。
//
// 設計メモ:
//  - クイズは開いた時点から表示する（読了直後にこそ定着するので、ボタンで
//    ワンクッション挟まない）。答えずに再開することもできる。
//  - キーは keydown だけを見る。keyup は controls.js のキー状態マップが使うので
//    絶対に止めない（押しっぱなしの解除が届かなくなる）。
//    なお game.js 側でポーズ中は controls.setEnabled(false) にしてあるため、
//    ここで矢印キーを潰す必要はない（本文スクロールに使わせる）。
import { ERA_LABELS } from './historicSitesData.js';

export function createSiteModal({ onResume, onAnswer } = {}) {
  const root = document.getElementById('site-modal');
  if (!root) {
    // モーダルの DOM が無い環境でもゲームは動かす（no-op 実装を返す）。
    console.warn('[siteModal] #site-modal が見つかりません');
    return { open() {}, close() {}, isOpen: () => false, dispose() {} };
  }

  const eraEl = document.getElementById('site-era');
  const placeEl = document.getElementById('site-place');
  const nameEl = document.getElementById('site-name');
  const nameEnEl = document.getElementById('site-name-en');
  const progressEl = document.getElementById('site-progress');
  const scrollEl = document.getElementById('site-scroll');
  const factsEl = document.getElementById('site-facts');
  const summaryEl = document.getElementById('site-summary');
  const quizEl = document.getElementById('site-quiz');
  const quizStatementEl = document.getElementById('site-quiz-statement');
  const quizResultEl = document.getElementById('site-quiz-result');
  const quizVerdictEl = document.getElementById('site-quiz-verdict');
  const quizExplainEl = document.getElementById('site-quiz-explain');
  const trueBtn = document.getElementById('quiz-true');
  const falseBtn = document.getElementById('quiz-false');
  const resumeBtn = document.getElementById('site-resume');
  const gameRoot = document.getElementById('game-root');

  let open = false;
  let current = null;
  let answered = false;
  let prevFocus = null;

  function addFact(label, value) {
    if (value == null || value === '') return;
    const row = document.createElement('div');
    row.className = 'fact-row';
    const dt = document.createElement('dt');
    dt.textContent = label;
    const dd = document.createElement('dd');
    dd.textContent = value;
    row.append(dt, dd);
    factsEl.append(row);
  }

  // answer() が付ける disabled / .correct / .wrong を打ち消す対称の処理。
  // quiz が無い（追悼施設）ときは節ごと隠す。
  function resetQuiz(quiz) {
    quizEl.hidden = !quiz;
    if (!quiz) return;
    quizStatementEl.textContent = quiz.statement;
    quizResultEl.hidden = true;
    quizVerdictEl.textContent = '';
    quizExplainEl.textContent = '';
    for (const b of [trueBtn, falseBtn]) {
      b.disabled = false;
      b.className = 'quiz-btn';
    }
  }

  function answer(choice) {
    if (!open || answered || !current?.quiz) return;
    answered = true;
    const correct = choice === current.quiz.answer;
    const chosenBtn = choice ? trueBtn : falseBtn;
    const correctBtn = current.quiz.answer ? trueBtn : falseBtn;

    trueBtn.disabled = true;
    falseBtn.disabled = true;
    // 不正解のときも正解側に印を付ける（何が正しかったかを必ず見せる）。
    correctBtn.classList.add('correct');
    if (!correct) chosenBtn.classList.add('wrong');

    quizVerdictEl.textContent = correct ? '正解！' : '不正解';
    quizVerdictEl.className = 'quiz-verdict ' + (correct ? 'correct' : 'wrong');
    quizExplainEl.textContent = current.quiz.explain ?? '';
    quizResultEl.hidden = false;

    onAnswer?.(current.id, correct);
    // 正誤と解説は本文の末尾にあるので、折り返しの下に隠れないよう送る。
    quizResultEl.scrollIntoView({ block: 'end', behavior: 'smooth' });
    resumeBtn.focus({ preventScroll: true });
  }

  function close() {
    if (!open) return;
    open = false;
    current = null;
    root.hidden = true;
    gameRoot?.classList.remove('modal-open');
    try { prevFocus?.focus?.({ preventScroll: true }); } catch { /* 消えていたら何もしない */ }
    prevFocus = null;
    onResume?.();
  }

  const onKeyDown = (e) => {
    if (!open) return;
    // Escape はここでは扱わない。main.js → game.handleEscape() → close() の1本に
    // 統一してある（main.js のリスナーの方が先に登録されるので必ず先に走る）。
    // Enter は再開。ただし ○× ボタンにフォーカスがあるときはブラウザ既定の
    // 「ボタン押下」に任せる。
    if (e.key === 'Enter' && document.activeElement !== trueBtn && document.activeElement !== falseBtn) {
      e.preventDefault();
      close();
      return;
    }
    if (!answered && current?.quiz) {
      if (e.key === '1' || e.key === 'o' || e.key === 'O') { e.preventDefault(); answer(true); return; }
      if (e.key === '2' || e.key === 'x' || e.key === 'X') { e.preventDefault(); answer(false); return; }
    }
    // フォーカストラップ: 背後の #audio-toggle などへ Tab で抜けさせない。
    if (e.key === 'Tab') {
      const focusables = [...root.querySelectorAll('button:not([disabled]), [tabindex="0"]')];
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const onTrue = () => answer(true);
  const onFalse = () => answer(false);
  const onResumeClick = () => close();

  trueBtn?.addEventListener('click', onTrue);
  falseBtn?.addEventListener('click', onFalse);
  resumeBtn?.addEventListener('click', onResumeClick);
  window.addEventListener('keydown', onKeyDown);

  return {
    // site は historicSitesData のレコード。meta は表示用の数値だけを渡す。
    open(site, { visitedCount = 0, totalCount = 0, alreadyVisited = false } = {}) {
      if (open) {
        console.warn('[siteModal] すでに開いています:', site?.id);
        return;
      }
      open = true;
      current = site;
      answered = false;
      prevFocus = document.activeElement;

      eraEl.textContent = ERA_LABELS[site.era] ?? '';
      eraEl.className = 'site-badge era-' + site.era;
      placeEl.textContent = `${site.emoji ?? ''} ${site.country}`.trim();
      nameEl.textContent = site.name;
      nameEnEl.textContent = site.nameEn;

      progressEl.textContent = `訪問 ${visitedCount} / ${totalCount}`;
      const badge = document.createElement('span');
      // 追悼施設（solemn）では「NEW!」のような祝う表現を出さない。
      badge.className = alreadyVisited ? 'site-again' : 'site-new';
      badge.textContent = alreadyVisited ? '訪問済み' : (site.solemn ? '初訪問' : 'NEW!');
      progressEl.append(badge);

      factsEl.innerHTML = '';
      addFact('年代', site.yearLabel);
      addFact('国・地域', site.country);
      addFact('文明・王朝', site.civilization);
      addFact('世界遺産', site.whcYear ? `${site.whcYear}年 登録${site.whcNote ? `（${site.whcNote}）` : ''}` : '—');
      for (const f of site.facts ?? []) addFact(f.label, f.value);

      summaryEl.textContent = site.summary;
      resetQuiz(site.quiz);

      root.hidden = false;
      gameRoot?.classList.add('modal-open');
      scrollEl.scrollTop = 0;
      // 本文にフォーカスを当てて、開いた直後から矢印キーでスクロールできるようにする。
      scrollEl.focus({ preventScroll: true });
    },
    close,
    isOpen: () => open,
    dispose() {
      window.removeEventListener('keydown', onKeyDown);
      trueBtn?.removeEventListener('click', onTrue);
      falseBtn?.removeEventListener('click', onFalse);
      resumeBtn?.removeEventListener('click', onResumeClick);
      open = false;
      current = null;
      root.hidden = true;
      gameRoot?.classList.remove('modal-open');
    },
  };
}
